// Plan 08-04 — MatchCanvas wrapper.
//
// Wraps `<GameCanvas playerView={...} />` from src/game/App.tsx. When the
// playerView prop is null (still loading, or no STATE_SNAPSHOT received
// yet), renders a "Loading the runes…" placeholder per UI-SPEC body-md.
//
// CRITICAL — client-side B12 firewall hold (NY-08-10): MatchCanvas reads
// state EXCLUSIVELY from the playerView prop. It NEVER consults a global
// store, NEVER inspects window.location for slot hints, NEVER falls back
// to URL parameters. The single trust boundary is the prop the parent
// (Plan 05's WS subscription) hands down.

import type { JSX, ReactNode } from 'react'
import { memo } from 'react'
import { FormattedMessage } from 'react-intl'
import { GameCanvas } from '../../game/App'
import type { BoardView } from '../../game/board-view'
import type { PlayerView } from '../../game/PlayerViewSchema'
import { Spinner } from '../components/Spinner'

interface MatchCanvasProps {
  playerView: PlayerView | null
  boardView: BoardView
  /**
   * Phase 31 Plan 31-03 — optional Pixi sub-layer rendered ABOVE LetterLayer
   * inside the @pixi/react World tree. Used by AnchorPlacementOverlay so the
   * Sun-Yellow scale-pulse and hit-areas live in the same coordinate system
   * as the letter cubes. The slot is opaque to MatchCanvas — it forwards to
   * GameCanvas which forwards to World.
   */
  overlayLayer?: ReactNode
}

// Wrapped in React.memo so the PixiJS scene graph only re-renders when its
// inputs (playerView ref, boardView, overlay layer) actually change —
// NOT on every MatchScreen re-render driven by $turnBuilder drafts (tapping
// WORD / tracing letters). The parent memoizes overlayLayer so the default
// shallow prop compare holds across those drafts.
function MatchCanvasImpl({
  playerView,
  boardView,
  overlayLayer,
}: MatchCanvasProps): JSX.Element {
  if (playerView === null) {
    return (
      <div
        data-screen-region="canvas-loading"
        role="status"
        aria-live="polite"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-4)',
          backgroundColor: 'var(--color-bg-mid)',
          fontFamily: 'Manrope, sans-serif',
          fontSize: '16px',
          color: 'var(--color-text-primary)',
        }}
      >
        <Spinner />
        <FormattedMessage
          id="match.loading"
          defaultMessage="Loading the runes…"
        />
      </div>
    )
  }

  return (
    <div data-screen-region="canvas" style={{ width: '100%', height: '100%' }}>
      <GameCanvas
        playerView={playerView}
        boardView={boardView}
        {...(overlayLayer !== undefined ? { overlayLayer } : {})}
      />
    </div>
  )
}

export const MatchCanvas = memo(MatchCanvasImpl)
